import { FieldType, TConsentField } from '../../types/types.ts';
import { TCmsField, TCmsMedia } from '../models/flow.ts';
import { PreferredWo, TWelcomeOffer } from '../models/welcomeOffer.ts';
import { findDocs } from '../payload/client.ts';
import { MEDIA_POPULATE, resolveMediaUrl } from '../payload/media.ts';

const COLLECTION = 'welcome_offers';

const OFFER_DEPTH = 2;

const OFFER_LIMIT = 100;

/** Payload-shaped welcome offer, internal to this repository. */
type TPayloadWelcomeOffer = {
  name?: string | null;
  vertical?: string | null;
  exclusive_welcome_offer?: boolean | null;
  valuable?: { thumbnail_image?: TCmsMedia; token_image?: TCmsMedia } | null;
  thumbnail_image?: TCmsMedia;
  token_image?: TCmsMedia;
  title?: string | null;
  widget_title?: string | null;
  select_button?: string | null;
  short_description?: string | null;
  registration_title?: string | null;
  significant_terms?: string | null;
  full_terms?: string | null;
  consent?: Pick<
    TCmsField,
    'fieldType' | 'fieldName' | 'title' | 'description' | 'textAlign' | 'isCheckable' | 'consents'
  > | null;
};

const thumbnailOf = (offer: TPayloadWelcomeOffer) =>
  resolveMediaUrl(offer.valuable?.thumbnail_image) || resolveMediaUrl(offer.thumbnail_image);

const tokenOf = (offer: TPayloadWelcomeOffer) =>
  resolveMediaUrl(offer.valuable?.token_image) || resolveMediaUrl(offer.token_image);

const adaptConsent = (consent: TPayloadWelcomeOffer['consent']): TConsentField => ({
  name:        consent?.fieldName?.name || '',
  label:       'Consent',
  fieldType:   consent?.fieldType as FieldType.ConsentField,
  title:       consent?.title || '',
  isCheckable: consent?.isCheckable || false,
  description: consent?.description || '',
  consents:    (consent?.consents ?? []).map(({ name, replaceWith, staticTermsName, link }) =>
    ({ name, replaceWith, staticTermsName, link })),
  textAlign: consent?.textAlign || 'left',
});

const adaptWelcomeOffers = (
  welcomeOffers: TPayloadWelcomeOffer[],
  preferred?: PreferredWo,
): TWelcomeOffer[] => {
  if (!welcomeOffers) {
    return [];
  }

  const adaptedOffers = welcomeOffers?.map((offer) => ({
    name:              offer.name || '',
    vertical:          offer.vertical || '',
    isExclusive:       offer.exclusive_welcome_offer === true,
    widgetTitle:       offer.widget_title || '',
    registrationTitle: offer.registration_title || '',
    title:             offer.title || '',
    shortDesc:         offer.short_description || '',
    selectButton:      offer.select_button || '',
    significantTerms:  offer.significant_terms || '',
    fullTerms:         offer.full_terms || '',
    thumbnailImage:    thumbnailOf(offer),
    tokenImage:        tokenOf(offer),
    consent:           adaptConsent(offer.consent),
  }));

  const norm = (s: string) => (s || '').toLowerCase();
  const isLiveCasino = (v: string) => norm(v).includes('live') && norm(v).includes('casino');
  const isCasino = (v: string) => norm(v).includes('casino') && !isLiveCasino(v);
  const isSports = (v: string) => norm(v).includes('sport');

  if (adaptedOffers?.length > 1) {
    const exclusiveOffers = adaptedOffers.filter(
      offer => offer?.isExclusive,
    );

    const exclusiveVerticals = new Set(
      exclusiveOffers.map(offer => norm(offer?.vertical)),
    );

    const nonExclusiveOffers = adaptedOffers.filter(
      offer =>
        !offer?.isExclusive &&
        !exclusiveVerticals.has(norm(offer?.vertical)),
    );

    if (preferred === 'sports') {
      const sports = nonExclusiveOffers.filter(o => isSports(o.vertical));
      const others = nonExclusiveOffers.filter(o => !isSports(o.vertical));

      return [...exclusiveOffers, ...sports, ...others];
    }

    if (preferred === 'live_casino') {
      const live = nonExclusiveOffers.filter(o => isLiveCasino(o.vertical));
      const casino = nonExclusiveOffers.filter(o => isCasino(o.vertical));
      const others = nonExclusiveOffers.filter(o => !isLiveCasino(o.vertical) && !isCasino(o.vertical));

      return [...exclusiveOffers, ...live, ...casino, ...others];
    }

    if (preferred === 'casino' || preferred === '' || preferred === undefined) {
      const casino = nonExclusiveOffers.filter(o => isCasino(o.vertical));
      const live = nonExclusiveOffers.filter(o => isLiveCasino(o.vertical));
      const others = nonExclusiveOffers.filter(o => !isCasino(o.vertical) && !isLiveCasino(o.vertical));

      return [...exclusiveOffers, ...casino, ...live, ...others];
    }

    const casinoDefault = nonExclusiveOffers.filter(o => isCasino(o.vertical) || isLiveCasino(o.vertical));
    const otherDefault = nonExclusiveOffers.filter(o => !isCasino(o.vertical) && !isLiveCasino(o.vertical));

    return [...exclusiveOffers, ...casinoDefault, ...otherDefault];
  }

  return adaptedOffers;
};

/**
 * Fetches the published welcome offers for a market, plus the exclusive
 * affiliate offer when an affiliate name is provided, then maps and orders them
 * into the app's domain model.
 */
export const getWelcomeOffers = async ({
  market,
  language,
  affiliateName,
  preferred,
}: {
  market: string;
  language: string;
  affiliateName?: string | null;
  preferred?: PreferredWo;
}): Promise<TWelcomeOffer[]> => {
  const [regular, affiliate] = await Promise.all([
    findDocs<TPayloadWelcomeOffer>(COLLECTION, {
      locale:   language,
      depth:    OFFER_DEPTH,
      limit:    OFFER_LIMIT,
      populate: MEDIA_POPULATE,
      where:    {
        'where[markets][in]':                         market,
        'where[exclusive_welcome_offer][not_equals]': 'true',
      },
    }),
    affiliateName
      ? findDocs<TPayloadWelcomeOffer>(COLLECTION, {
        locale:   language,
        depth:    OFFER_DEPTH,
        limit:    OFFER_LIMIT,
        populate: MEDIA_POPULATE,
        where:    {
          'where[name][equals]':                    affiliateName,
          'where[exclusive_welcome_offer][equals]': 'true',
        },
      })
      : Promise.resolve([]),
  ]);

  return adaptWelcomeOffers([...regular, ...affiliate], preferred);
};
